import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChatContainerComponent, GalaxyChatModule } from '@vendasta/galaxy/chat';
import { GalaxyChatComposerModule } from '@vendasta/galaxy/chat-composer';
import { ChatComposerComponent, SentMessage } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { Namespace } from '@vendasta/protos/ai_assistants/v1alpha1';
import { catchError, EMPTY, take } from 'rxjs';

import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { AiProposedActionComponent } from '../ai-proposed-action/ai-proposed-action.component';
import { ProposedActionApproval, ProposedActionRejection } from '../ai-proposed-action/ai-proposed-action.types';
import { PlanDisplayComponent } from '../plan-display/plan-display.component';
import { QuestionsDisplayComponent } from '../questions-display/questions-display.component';
import { AskQuestion } from '../questions-display/questions-display.types';
import { GenerationChatHistoryComponent } from '../generation-chat-history/generation-chat-history.component';
import { SessionHistoryService } from '../generation-chat-history/session-history.service';
import { GenerationChatService } from './generation-chat.service';
import { WorkingMessage } from './generation-chat.types';

@Component({
  selector: 'ai-generation-chat',

  imports: [
    GalaxyChatModule,
    GalaxyChatComposerModule,
    GalaxyLoadingSpinnerModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    AiProposedActionComponent,
    PlanDisplayComponent,
    QuestionsDisplayComponent,
    GenerationChatHistoryComponent,
    AiAssistantI18nModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './generation-chat.component.html',
  styleUrl: './generation-chat.component.scss',
})
export class GenerationChatComponent {
  readonly placeholder = input<string>('Ask a question...');
  readonly senderName = input<string>('Generator');
  readonly senderIcon = input<string>('smart_toy');
  readonly chatId = input<string>('generation-chat');
  readonly hints = input<string[]>([]);
  readonly title = input<string>('');
  readonly closable = input<boolean>(false);

  readonly closed = output<void>();

  protected readonly chatService = inject(GenerationChatService);
  private readonly historyService = inject(SessionHistoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly chatContainer = viewChild<ChatContainerComponent>('chatContainer');
  private readonly composer = viewChild<ChatComposerComponent>('composer');

  protected readonly historyPanelOpen = signal(false);
  protected readonly historyEnabled = computed(() => this.chatService.extraTags().length > 0);
  protected readonly autoLoadResolved = signal(false);
  private lastAutoLoadedTagsKey: string | null = null;

  constructor() {
    effect(() => {
      if (!this.historyEnabled()) {
        this.autoLoadResolved.set(true);
        return;
      }
      if (!this.chatService.userId()) {
        this.autoLoadResolved.set(true);
        return;
      }
      const namespace = this.chatService.namespace();
      const filterTags = this.chatService.sessionTags();
      if (!namespace || filterTags.length === 0) return;
      const tagsKey = filterTags.join('|');
      if (tagsKey === this.lastAutoLoadedTagsKey) return;
      if (this.chatService.messages().length > 0) {
        this.autoLoadResolved.set(true);
        return;
      }
      this.lastAutoLoadedTagsKey = tagsKey;
      this.autoLoadResolved.set(false);
      this.autoLoadMostRecent(namespace, filterTags);
    });
  }

  private autoLoadMostRecent(namespace: Namespace, tags: string[]): void {
    this.historyService
      .listSessions$(namespace, tags, { pageSize: 1, cursor: '' })
      .pipe(
        take(1),
        catchError(() => {
          this.autoLoadResolved.set(true);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        const first = page.sessions[0];
        if (!first || this.chatService.messages().length > 0) {
          this.autoLoadResolved.set(true);
          return;
        }
        this.chatService
          .loadSession(first.sessionId)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            complete: () => this.autoLoadResolved.set(true),
          });
      });
  }

  notifyNewMessageSent(): void {
    this.chatContainer()?.onNewMessageSent();
  }

  onHintClicked(hint: string): void {
    const composer = this.composer();
    if (!composer) return;
    composer.clearMessageText();
    composer.insertDynamicContent(hint, 'after');
  }

  onSend(event: SentMessage): void {
    if (!event.messageText?.trim()) return;
    this.chatService.sendMessage(event.messageText);
    this.notifyNewMessageSent();
  }

  onPlanApproved(plan: string): void {
    this.chatService.approvePlan(plan);
    this.notifyNewMessageSent();
  }

  onPlanRejected(): void {
    this.chatService.rejectPlan();
    this.notifyNewMessageSent();
  }

  onQuestionsAnswered(questions: AskQuestion[], answers: Record<number, string>): void {
    const compiled = questions.map((q, i) => `${q.text}: ${answers[i] ?? ''}`).join('\n');
    this.chatService.answerQuestions(compiled);
    this.notifyNewMessageSent();
  }

  onQuestionsCancelled(): void {
    this.chatService.sendMessage('questions cancelled');
    this.notifyNewMessageSent();
  }

  onProposedActionApproved(event: ProposedActionApproval, actionComponent: AiProposedActionComponent): void {
    this.chatService.approveProposedAction(event, actionComponent);
  }

  onProposedActionRejected(event: ProposedActionRejection, actionComponent: AiProposedActionComponent): void {
    this.chatService.rejectProposedAction(event, actionComponent);
  }

  onProposedActionChangeRequested(): void {
    this.composer()?.focusTextArea();
  }

  onClose(): void {
    this.closed.emit();
  }

  openHistoryPanel(): void {
    this.historyPanelOpen.set(true);
  }

  closeHistoryPanel(): void {
    this.historyPanelOpen.set(false);
  }

  onHistorySessionSelected(sessionId: string): void {
    this.closeHistoryPanel();
    this.chatService.loadSession(sessionId).pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onHistoryNewChatRequested(): void {
    this.closeHistoryPanel();
    this.chatService.startNewSession();
  }

  protected allResolved(message: WorkingMessage): boolean {
    return message.toolCalls.every((tc) => tc.state !== 'running');
  }

  protected preserveNewlines(text: string): string {
    return text.replace(/\r?\n/g, '<br>');
  }
}
